import { faDownload, faTrash } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LoadingButton } from '@mui/lab';
import { AlertTitle, Box, Button, Card, CardContent, CircularProgress, Grid, styled, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography, useTheme } from '@mui/material';
import { FC, useContext, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { GetUserGroupsConfigResponse, PostRemoveCommunityGroupDocument, PostUploadCommunityGroupDocument, User } from '../../api/dtos/dtos';
import FileInput from '../../components/common/FileInput';
import { ERROR_TIMEOUT, SUCCESS_TIMEOUT } from '../../constants/styles/AlertConstants';
import ServiceStackClientContext from '../../contexts/api/ServiceStackContext';
import AuthContext from '../../contexts/auth/AuthContext';
import MapContext, { MapFilter } from '../../contexts/data/MapContext';
import UserGroupsContext from '../../contexts/data/UserGroupsContext';
import AlertContext from '../../contexts/styles/AlertContext';
import useGetCommunityGroupDocuments from '../../hooks/data/useGetCommunityGroupDocuments';
import { faArrowLeft } from '@fortawesome/pro-duotone-svg-icons';
import { AppSettings } from '../../AppSettings';
import isCurrentGroupAdmin from '../../functions/user';

const ContentWrapper = styled(Box)(({ theme }) => ({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    backgroundColor: theme.palette.background.default,
}));

const ManageGroupDocumentsComponent: FC = () => {
    let navigate = useNavigate();
    const theme = useTheme();

    //
    // -- Params --
    const { species: speciesName } = useParams();

    //
    // -- Contexts --
    const { filter } = useContext(MapContext);
    const { addAlert } = useContext(AlertContext);
    const client = useContext(ServiceStackClientContext);
    const { user } = useContext(AuthContext);
    const { data: userGroupsConfig } = useContext(UserGroupsContext);

    //
    // -- States --
    const [deleteDocumentsLoading, setDeleteDocumentsLoading] = useState<Record<number, boolean>>({});
    const [uploadDocumentLoading, setUploadDocumentLoading] = useState<boolean>(false);
    const [file, setFile] = useState<File | null>(null);
    const [fileNameInput, setFileNameInput] = useState<string>("");

    // Generate the community group documents request
    const communityGroupDocumentsRequest = useMemo(() => {
        if (filter.bySubmitter?.communityGroupId){
            return {
                communityGroupId: filter.bySubmitter.communityGroupId,
            };
        }
    }, [filter]);

    // Get community group documents request
    const { data: communityGroupDocuments, loading: communityGroupDocumentsLoading, refresh: useGetCommunityGroupDocumentsRefresh }  = useGetCommunityGroupDocuments(
        communityGroupDocumentsRequest, 
        { revalidateOnFocus: false, errorRetryCount: 0 }
    );

    // Sets the document to obsolete
    const deleteDocument = async (documentId: number, communityGroupId: number) => {
        if (documentId === undefined) {
            return;
        }

        setDeleteDocumentsLoading(c => ({...c, [documentId]: true}));

        try {
            await client.post(new PostRemoveCommunityGroupDocument({
                id: documentId,
                communityGroupId: communityGroupId
            }));

            await useGetCommunityGroupDocumentsRefresh();
        }
        catch {
            addAlert({
                content: <AlertTitle>
                            An error occurred while attempting to delete document.
                        </AlertTitle>,
                dismissable: true,
                severity: 'error',
                timeout: ERROR_TIMEOUT
            })
        }
        finally {
            setDeleteDocumentsLoading(c => ({...c, [documentId]: false}));
        }
    };

    const handleFileNameInputChange = (event: any) => {
        setFileNameInput(event.target.value);
    };

    const handleFileSelect = (file?: File) => {
        if (file) {
            setFile(file);
        }
        else {
            setFile(null);
        }
    };

    // Converts a file to a byte string
    function readFileAsByteString(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (event) => {
                const result = event.target?.result;
                if (result && typeof result !== 'string') {
                    const byteArray = new Uint8Array(result);
                    const byteString = arrayBufferToBase64(byteArray);
                    resolve(byteString);
                } else {
                    reject(new Error('File could not be read as an ArrayBuffer'));
                }
            };
            reader.onerror = () => {
                reject(new Error('Error reading file'));
            };
            reader.readAsArrayBuffer(file);
        });
    }
    
    function arrayBufferToBase64(buffer: Uint8Array): string {
        let binary = '';
        const bytes = new Uint8Array(buffer);
        const len = bytes.byteLength;
        for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return btoa(binary);
    }

    const submitFile = async (
        file: File | null, 
        user: User | null, 
        userGroupsConfig: GetUserGroupsConfigResponse | undefined, 
        filter: MapFilter,
        fileName: string
    ) => {
        if (!file){
            addAlert({
                content: <AlertTitle>
                            Please upload a document to submit.
                        </AlertTitle>,
                dismissable: true,
                severity: 'info',
                timeout: SUCCESS_TIMEOUT
            });
            return;
        }

        if (!user){
            return;
        }

        // Validation
        // If user is not community group admin
        if (!isCurrentGroupAdmin(userGroupsConfig, user, filter)){
            addAlert({
                content: <AlertTitle>
                            User does not have permission to submit a document.
                        </AlertTitle>,
                dismissable: true,
                severity: 'error',
                timeout: ERROR_TIMEOUT
            });
            return;
        }
        // If file size is too big (limit = 50MB)
        if (file!.size > 50000000) {
            addAlert({
                content: <AlertTitle>
                            File size is too large.
                        </AlertTitle>,
                dismissable: true,
                severity: 'error',
                timeout: ERROR_TIMEOUT
            });
            return;
        }
        // If number of documents exceeds community group limit (limit = 20)
        if (communityGroupDocuments && communityGroupDocuments?.length >= 20){
            addAlert({
                content: <AlertTitle>
                            The group has reached its maximum document limit.
                        </AlertTitle>,
                dismissable: true,
                severity: 'error',
                timeout: ERROR_TIMEOUT
            });
            return;
        }
        if (fileName === ""){
            addAlert({
                content: <AlertTitle>
                            Please enter a file name.
                        </AlertTitle>,
                dismissable: true,
                severity: 'error',
                timeout: ERROR_TIMEOUT
            });
            return;
        }

        setUploadDocumentLoading(true);

        try {
            readFileAsByteString(file!).then(async (bytString) => {
                await client.post(new PostUploadCommunityGroupDocument({
                    communityGroupId: filter.bySubmitter.communityGroupId,
                    fileContent: bytString,
                    fileName: file!.name,
                    name: fileName
                }));

                await useGetCommunityGroupDocumentsRefresh();

            }).catch((error) => {
                console.error('Error reading file:', error);
            });
        }
        catch {
            addAlert({
                content: <AlertTitle>
                            An error occurred while attempting to submit the document.
                        </AlertTitle>,
                dismissable: true,
                severity: 'error',
                timeout: ERROR_TIMEOUT
            })
        }
        finally {
            setFile(null);
            setFileNameInput("");
            setUploadDocumentLoading(false);
        }
    }

    const generateDownloadLink = (communityGroupId: number, fileName: string) => {
        return `${AppSettings.baseUrl}/communitygroup/documents/download?CommunityGroupId=${communityGroupId}&FileName=${fileName}`
    };

    return (
        <Box>
            <ContentWrapper>
                {/* Header */}
                <Typography fontSize={32} color="#FFFFFF" sx={{paddingTop: '15px'}}>Manage Group Documents</Typography>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'start',
                        color: 'white'
                    }}
                >
                    <Button
                        variant="text"
                        sx={{
                            color: 'white',
                            fontSize: '0.65rem',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.15)'
                            }
                        }}
                        startIcon={
                            <FontAwesomeIcon
                                icon={faArrowLeft} 
                                style={{ 
                                    color: 'white',
                                    fontSize: '0.65rem',
                                    paddingRight: '3px' 
                                }}
                            />
                        }
                        onClick={() => navigate("/" + speciesName + "/datamanagement")}
                    >
                        Back to Data Management
                    </Button>
                </Box>

                <Card variant='outlined' sx={{marginTop: '20px', marginBottom: '20px'}}>
                    <CardContent>
                        <Grid container spacing={1} alignItems="center">
                            <Grid item xs={12}>
                                <Box display="flex" alignItems="center" paddingTop="10px" paddingBottom="20px">
                                    <Typography paddingLeft="5px" fontSize={24}>
                                        Manage Group Documents
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FileInput
                                    selectedFile={file}
                                    onFileSelect={handleFileSelect} 
                                />
                            </Grid>
                            <Grid item xs={0} md={6}></Grid>
                            <Grid item xs={12} md={6}>
                            <TextField 
                                    id="filled-basic" 
                                    label="File name" 
                                    variant="filled"
                                    size="small"
                                    sx={{ width: '100%' }}
                                    value={fileNameInput}
                                    onChange={handleFileNameInputChange} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <LoadingButton
                                    variant="contained"
                                    loading={uploadDocumentLoading}
                                    onClick={() => submitFile(file, user, userGroupsConfig, filter, fileNameInput)}
                                >
                                    Submit
                                </LoadingButton>
                            </Grid>
                        </Grid>

                        <Box height={'20px'} />

                        {(communityGroupDocumentsLoading) && (
                            <Box sx={{display: "flex", justifyContent: "center", width: "auto", padding: "20px"}}>
                                <CircularProgress />
                            </Box>
                        )}

                        {(!communityGroupDocumentsLoading && communityGroupDocuments && communityGroupDocuments?.length > 0) && (
                            <Table>
                                <TableHead>
                                    <TableRow sx={{backgroundColor: '#f0f0f0'}}>
                                        <TableCell align="left"><Typography fontSize={16} fontWeight={600}>Document Name</Typography></TableCell>
                                        <TableCell align="left"><Typography fontSize={16} fontWeight={600}>File Name</Typography></TableCell>
                                        <TableCell align="center"><Typography fontSize={16} fontWeight={600}>Download</Typography></TableCell>
                                        <TableCell align="center"><Typography fontSize={16} fontWeight={600}>Delete</Typography></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {communityGroupDocuments?.map(document => (
                                        <TableRow key={`${document.id}-${document.fileName}`}>
                                            <TableCell align="left">{document.name}</TableCell>
                                            <TableCell align="left">{document.fileName}</TableCell>
                                            <TableCell align="center">
                                                <Button
                                                    variant="text"
                                                    size="small"
                                                    href={generateDownloadLink(document.communityGroupId, document.fileName)}
                                                    target="_blank"
                                                >
                                                    <FontAwesomeIcon icon={faDownload} style={{ fontSize: '1.2rem', color: theme.palette.primary.main }} />
                                                </Button>
                                            </TableCell>
                                            <TableCell align="center">
                                                <LoadingButton
                                                    loading={deleteDocumentsLoading[document.id]}
                                                    size="small"
                                                    onClick={() => deleteDocument(document.id, document.communityGroupId)}
                                                >
                                                    <FontAwesomeIcon icon={faTrash} style={{ fontSize: '1.2rem', color: theme.palette.primary.main }} />
                                                </LoadingButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}

                        <Box height="20px" />
                    </CardContent>
                </Card>
                
            </ContentWrapper>
        </Box>
    )
}

const ManageGroupDocumentsPage: FC = () => {
    const { user } = useContext(AuthContext);
    const { species: speciesName } = useParams();

    if (!user) {
        return <Navigate to={"/" + speciesName + "/map"} replace />
    }
    return (<ManageGroupDocumentsComponent />)
}

export default ManageGroupDocumentsPage

